"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.metricsBrowserConsoleWriter = exports.standardBrowserConsoleWriter = void 0;
// NOTE: this is used by pino-pretty to format dates, so we're using it here as well
const dateformat_1 = __importDefault(require("dateformat"));
const jsonExtractorRegExp = new RegExp(/{(?:[^{}]|({(?:[^{}]|({(?:[^{}]|())*}))*}))*}/g);
/**
 * Log levels
 */
var LogLevels;
(function (LogLevels) {
    LogLevels["TRACE"] = "trace";
    LogLevels["DEBUG"] = "debug";
    LogLevels["INFO"] = "info";
    LogLevels["WARN"] = "warn";
    LogLevels["ERROR"] = "error";
    LogLevels["FATAL"] = "fatal";
})(LogLevels || (LogLevels = {}));
/**
 * Adds color to the log message output, this currently assumes that the output will have three colors:
 * 1. the color of the log level "pill" background
 * 2. the color of the location of where the log came from
 * 3. the color of the log message text
 *
 * @param message the message to log
 * @param backgroundColor the background color of the log level pill
 * @param additionalStyles any additional styles
 * @param additionalObjects any additional objects to log.
 */
function logColor(message, backgroundColor, additionalStyles, additionalObjects) {
    return [
        message,
        `background-color: ${backgroundColor}; padding: 2px 3px; border-radius: 3px; color: white; ${additionalStyles}`,
        'color: graytext',
        'color: canvastext',
        ...(additionalObjects !== null && additionalObjects !== void 0 ? additionalObjects : [])
    ];
}
/**
 * Formatter for standard log messages.
 *
 * @param logMessage the log message
 * @param level the log level
 * @param backgroundColor the background color of the log level pill
 * @param additionalStyles any additional objects to log
 */
function standardLogMessageFormatter(logMessage, level, backgroundColor, additionalStyles) {
    var _a, _b, _c;
    const { name, msg } = logMessage;
    /**
     * This is a hack to work around a bug in Pino where if you provide a custom "write" function
     * they will always pass a serialized copy of the log message with any JSON objects
     * stringified into the message. To work around this limitation we attempt to use regex to replace
     * any JSON strings inside the message with %O. We then pass the parsed JSON objects to the
     * browsers console command, so they can be rendered in the console as an interactive object.
     */
    const updatedLogMessage = (_a = msg === null || msg === void 0 ? void 0 : msg.replace(jsonExtractorRegExp, '%O')) !== null && _a !== void 0 ? _a : '';
    const extractedObjects = (_c = (_b = msg === null || msg === void 0 ? void 0 : msg.match(jsonExtractorRegExp)) === null || _b === void 0 ? void 0 : _b.map(jsonString => {
        try {
            return JSON.parse(jsonString);
        }
        catch (_a) {
            return jsonString;
        }
    })) !== null && _c !== void 0 ? _c : [];
    const time = (0, dateformat_1.default)(Date.now(), 'HH:MM:ss.l');
    return logColor(`[${time}] %c${level}%c (${name}): %c${updatedLogMessage}`, backgroundColor, additionalStyles, extractedObjects);
}
/**
 * Formatter for metric log messages.
 *
 * @param logMessage the log message
 * @param level the log level
 * @param backgroundColor the background color of the log level pill
 * @param additionalStyles any additional objects to log
 */
function metricsLogMessageFormatter(logMessage, level, backgroundColor, additionalStyles) {
    const { label, name, value } = logMessage;
    const time = (0, dateformat_1.default)(Date.now(), 'HH:MM:ss.l');
    const message = `The [${name}] metric took ${value.toFixed(0)}ms to complete.`;
    return logColor(`[${time}] %c${level}%c (${label}): %c${message}`, backgroundColor, additionalStyles);
}
/**
 * Creates a log writer to be used with pino.
 *
 * @param console the console
 * @param formatter the formatter used to format log message output
 * @param level the log level
 * @param backgroundColor the background color of the log level pill
 * @param additionalStyles any additional objects to log
 */
function createLogger(console, formatter, level, backgroundColor, additionalStyles) {
    return function logger(logObject) {
        const command = level === LogLevels.FATAL ? 'error' : level;
        try {
            console[command](...formatter(logObject, level.toUpperCase(), backgroundColor, additionalStyles));
        }
        catch (error) {
            console.error(error);
        }
    };
}
/**
 * Creates a log writer for use in the browser console
 *
 * @param console the console
 * @param formatter the formatter used to format log message output
 */
function browserConsoleWriter(console, formatter) {
    return {
        trace: createLogger(console, formatter, LogLevels.TRACE, 'magenta'),
        debug: createLogger(console, formatter, LogLevels.DEBUG, 'blue'),
        info: createLogger(console, formatter, LogLevels.INFO, 'green'),
        warn: createLogger(console, formatter, LogLevels.WARN, 'orange'),
        error: createLogger(console, formatter, LogLevels.ERROR, 'red'),
        fatal: createLogger(console, formatter, LogLevels.FATAL, 'red', 'font-weight: bold')
    };
}
/**
 * Creates a browser console writer for standard log messages.
 *
 * @param console the console
 */
function standardBrowserConsoleWriter(console) {
    return browserConsoleWriter(console, standardLogMessageFormatter);
}
exports.standardBrowserConsoleWriter = standardBrowserConsoleWriter;
/**
 * Creates a browser console writer for metric log messages.
 *
 * @param console the console
 */
function metricsBrowserConsoleWriter(console) {
    return browserConsoleWriter(console, metricsLogMessageFormatter);
}
exports.metricsBrowserConsoleWriter = metricsBrowserConsoleWriter;
//# sourceMappingURL=BrowserConsoleWriter.js.map